import { APIKit } from 'config/axios/index';
import store from 'config/redux/index';
import { start, end } from 'config/redux/slice';

import { ServerConstants, ServiceConstants } from 'constant';
import { ServiceModel } from 'models';

import { handleErrorWithTokenRenewal } from './common';
import * as PublicService from './public';

const { API_V1_TODO } = ServerConstants;
const {
  REQUEST_TODO_LIST_ID,
  REQUEST_TODO_VIEW_ID,
  REQUEST_TODO_CREATE_ID,
  REQUEST_TODO_DELETE_ID,
  REQUEST_TODO_UPDATE_PROPERTY_ID,
  REQUEST_TODO_UPDATE_MULTIPLE_ID,
  REQUEST_TODO_COMPLETED_ID,
  REQUEST_TODO_UNCOMPLETED_ID,
  REQUEST_TODO_OPTIONS_ID,
  REQUEST_TODO_NUMBER_ID,
} = ServiceConstants;

export interface TodoUserRequest {
  userId: number;
}

export interface TodoCreateRequest {
  title: string;
  description?: string;
  type: 'CALL' | 'MEETING' | 'INTERNAL_TASK';
  timeType: 'DEADLINE' | 'EXACT_TIME' | 'INTERVAL';
  completionType: 'EVERYONE' | 'ANYONE';
  urgent: boolean;
  important: boolean;
  archive?: boolean;
  dueDate?: string;
  startTime?: string;
  endTime?: string;
  recurring: boolean;
  recurringType?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  clientId?: number;
  users?: TodoUserRequest[];
}

export interface TodoUpdateProperty {
  name: string;
  value: any;
}

export interface TodoListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  dateFilter?: 'TODAY' | 'NEXT_7_DAYS';
  type?: 'CALL' | 'MEETING' | 'INTERNAL_TASK';
  urgent?: boolean;
  important?: boolean;
  myTodo?: boolean;
  archive?: boolean;
  absence?: boolean;
}

export interface TodoListResponse {
  content: any[];
  totalElements: number;
  totalPages: number;
  page: number;
}

function buildTodoQueryString(params?: TodoListParams) {
  const searchParams = new URLSearchParams();

  if (params?.page !== undefined) {
    searchParams.append('page', String(params.page));
  }
  if (params?.pageSize !== undefined) {
    searchParams.append('pageSize', String(params.pageSize));
  }
  if (params?.search) {
    searchParams.append('search', params.search);
  }
  if (params?.dateFilter) {
    searchParams.append('dateFilter', params.dateFilter);
  }
  if (params?.type) {
    searchParams.append('type', params.type);
  }
  if (params?.urgent !== undefined) {
    searchParams.append('urgent', String(params.urgent));
  }
  if (params?.important !== undefined) {
    searchParams.append('important', String(params.important));
  }
  if (params?.myTodo !== undefined) {
    searchParams.append('myTodo', String(params.myTodo));
  }
  if (params?.archive !== undefined) {
    searchParams.append('archive', String(params.archive));
  }
  if (params?.absence !== undefined) {
    searchParams.append('absence', String(params.absence));
  }

  return searchParams.toString();
}

function buildTodoUrl(params?: TodoListParams) {
  const queryString = buildTodoQueryString(params);
  return queryString ? `${API_V1_TODO}?${queryString}` : API_V1_TODO;
}

function buildTodoNumberUrl(params?: TodoListParams) {
  const queryString = buildTodoQueryString(params);
  return queryString ? `${API_V1_TODO}/number?${queryString}` : `${API_V1_TODO}/number`;
}

function normalizeTodoNumber(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export async function todoList(params?: TodoListParams) {
  const requestId = REQUEST_TODO_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(buildTodoUrl(params));
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoList, [params], requestId);
  }
}

export async function todoOne(id: number) {
  const requestId = REQUEST_TODO_VIEW_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_TODO}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoOne, [id], requestId);
  }
}

export async function todoCreate(data: TodoCreateRequest) {
  const requestId = REQUEST_TODO_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(API_V1_TODO, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoCreate, [data], requestId);
  }
}

export async function todoUpdate(id: number, data: TodoCreateRequest) {
  const requestId = REQUEST_TODO_UPDATE_PROPERTY_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.put(`${API_V1_TODO}/${id}`, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoUpdate, [id, data], requestId);
  }
}

export async function todoDelete(id: number) {
  const requestId = REQUEST_TODO_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_TODO}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoDelete, [id], requestId);
  }
}

export async function todoUpdateProperty(id: number, property: TodoUpdateProperty) {
  const requestId = REQUEST_TODO_UPDATE_PROPERTY_ID + id + property.name;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_TODO}/${id}`, property);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoUpdateProperty, [id, property], requestId);
  }
}

export async function todoUpdateMultiple(id: number, properties: TodoUpdateProperty[]) {
  const requestId = REQUEST_TODO_UPDATE_MULTIPLE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_TODO}/${id}/multiple`, properties);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoUpdateMultiple, [id, properties], requestId);
  }
}

export async function todoMarkCompleted(id: number) {
  const requestId = REQUEST_TODO_COMPLETED_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_TODO}/${id}/completed`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoMarkCompleted, [id], requestId);
  }
}

export async function todoMarkUncompleted(id: number) {
  const requestId = REQUEST_TODO_UNCOMPLETED_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_TODO}/${id}/uncompleted`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoMarkUncompleted, [id], requestId);
  }
}

export async function todoOptions() {
  const requestId = REQUEST_TODO_OPTIONS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_TODO}/options`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoOptions, [], requestId);
  }
}

export async function todoNumber(params?: TodoListParams) {
  const requestId = REQUEST_TODO_NUMBER_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(buildTodoNumberUrl(params));
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, todoNumber, [params], requestId);
  }
}

export async function todoNumberValue(
  params?: TodoListParams,
  hasRetried = false,
): Promise<number> {
  try {
    const response = await APIKit.get(buildTodoNumberUrl(params));
    return normalizeTodoNumber(response.data);
  } catch (err: any) {
    const status = err?.status ?? err?.response?.status;

    if (status === 401 && !hasRetried) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return todoNumberValue(params, true);
      }
    }

    return 0;
  }
}
